import { ORG_SEED_ORG_ID_PATTERN, orgSeedsSchema } from "./org-seed.js";
import { z } from "zod";
//#region src/org-seed/org-seed-door.ts
/**
* The seed door: the body and the answer of `PUT /internal/v1/org-seeds` on core-api.
*
* Plan `2026-10-16-customer-onboarding`, stage S3, rulings R-034 item 3, R-035 and R-036 item 1,
* defaults F1, F3 and F8.
*
* Who uses it:
*
* - zion-operator (WS-10) reads the envelope member `orgSeeds`, splits it with
*   {@link orgSeedsIngestRequests} and sends each part to {@link ORG_SEEDS_INGEST_ROUTE} with its
*   operator token, as it sends `PUT /internal/v1/app-offers`. It reads the answer with
*   {@link OrgSeedsIngestResultSchema} and {@link orgSeedsIngestResultWithin}, so a partial answer
*   is valid (R-046 item 1). It sends a seed again while the seed's outcome is not final
*   ({@link isFinalOrgSeedOutcome}) or the answer left the seed out: at the next report when the
*   owed list changed, else after its retry hold of 300,000 ms (`APP_OFFER_INGEST_RETRY_MS`). It
*   sends a seed with a final outcome again after its ledger hold of one hour (R-039 item 2).
* - core-api (WS-9) binds {@link OrgSeedsIngestRequestSchema} with `@Body({ schema })` on the door,
*   behind `OperatorTokenGuard`, and answers one {@link OrgSeedResult} per seed that did not throw.
*   A seed that throws stays out of the answer (R-045 item 3).
*
* The id space: a seed's `orgId` is the CP org id, and the data plane org that the seed makes takes
* that id as its own (D11). So the door body and the answer carry the same id as the envelope, and
* the operator program re-keys nothing.
*
* Strict at every depth. No `.transform()`, so `createOpenApiDocument()` can represent each schema.
* Pure: no clock, no filesystem, no network. No schema here admits a secret: a seed's `provider` is
* the view form of the sign-in choice (F8).
*/
/** core-api's route of the seed door: `PUT /internal/v1/org-seeds` (R-035 item 1). */
const ORG_SEEDS_INGEST_ROUTE = "/internal/v1/org-seeds";
/**
* The most seeds one call of the seed door carries: 32.
*
* The largest seed that `orgSeedSchema` admits is under 14,500 bytes of compact JSON: 32 domains of
* 253 characters, 64 app slugs of 63 characters, a 254-character owner email and an `oidc` provider
* with a 512-character issuer and a 256-character client id that JSON escapes to 512 bytes. So 32
* such seeds fit {@link ORG_SEEDS_INGEST_BODY_LIMIT_BYTES}. The operator program splits a longer
* envelope list over several calls with {@link orgSeedsIngestRequests}.
*/
const ORG_SEEDS_PER_CALL_MAX = 32;
/**
* The largest body, in bytes, that core-api's own JSON parser on the seed door admits: 512 KiB
* (524,288 bytes), the limit of `PUT /internal/v1/app-offers`. core-api answers 413 for a longer
* body before the guard runs (WS-9). Every body that {@link orgSeedsIngestRequests} builds fits.
*/
const ORG_SEEDS_INGEST_BODY_LIMIT_BYTES = 524288;
/**
* The body of `PUT /internal/v1/org-seeds`: one member, `orgSeeds`, 1 to
* {@link ORG_SEEDS_PER_CALL_MAX} seeds under the rules of `orgSeedsSchema` (ascending by `orgSlug`,
* no slug twice, no org id twice, each seed strict and at seed version 1).
*
* Any part of an envelope list that keeps its order is valid, so the caller may split one list over
* several calls. core-api's strict pipe answers 400 for the whole body, and writes nothing, for each
* of these:
* - an absent `orgSeeds`, or an unknown key beside it;
* - an empty list (F3: the operator program sends nothing when the envelope has no seed, R-036
*   item 1);
* - more than {@link ORG_SEEDS_PER_CALL_MAX} seeds;
* - a seed that `orgSeedSchema` refuses, such as a `clientSecret` key in `provider` or an unknown
*   seed version;
* - seeds out of `orgSlug` order, or a repeated slug or org id.
*/
const OrgSeedsIngestRequestSchema = z.strictObject({ orgSeeds: orgSeedsSchema.min(1).max(32) });
/**
* The outcome of one seed at the seed door (R-035 items 1, 3 and 5).
*
* - `seeded`: this call made the org from the seed: the org row with the seed's id and slug, its
*   signing key, built-in roles, default channels, provider row and owner invitation.
* - `present`: an org row with the seed's id was there before the call. When the org holds an
*   owner, a user with the owner address or an unexpired pending invitation for that address, the
*   call wrote no piece of the list above (R-039 item 2). Else the call made each missing piece of
*   the list above and changed no piece that was there.
* - `slug_conflict`: no org row has the seed's id, and another org row holds the seed's slug. The
*   call wrote nothing.
* - `awaiting_secret`: the seed's provider takes a client secret, and the data plane Secret that
*   `orgSeedClientSecretName` names is absent. The call wrote nothing.
* - `unsupported_provider`: the seed's provider type is outside core-api's provider catalog, such as
*   `oidc` and `workos` before stage S5. The call wrote nothing.
*
* core-api checks in this order and answers the first that holds: `present` for an org that holds
* its owner as above, then `unsupported_provider`, `awaiting_secret`, `slug_conflict`, then
* `present` or `seeded`. Only `seeded` and `present` are final ({@link ORG_SEED_FINAL_OUTCOMES}).
*/
const ORG_SEED_OUTCOMES = [
	"seeded",
	"present",
	"slug_conflict",
	"awaiting_secret",
	"unsupported_provider"
];
/**
* The final outcomes: `seeded` and `present`. After one, the operator program holds the seed as done
* and sends it again only after its ledger hold of one hour ends (R-039 item 2).
*/
const ORG_SEED_FINAL_OUTCOMES = ["seeded", "present"];
/**
* Tell whether an outcome is final.
*
* Caller: the operator program, which keeps a seed owed while its outcome is not final (R-035
* item 1). It sends that seed again at the next report when the owed list changed, else after its
* retry hold of 300,000 ms (`APP_OFFER_INGEST_RETRY_MS`). Input: any {@link OrgSeedOutcome}.
* Output: `true` for `seeded` and `present`, `false` for the other three. It never throws.
*/
function isFinalOrgSeedOutcome(outcome) {
	return ORG_SEED_FINAL_OUTCOMES.includes(outcome);
}
/**
* The answer for one seed: `{ orgId, outcome }`. `orgId` is the seed's org id, a lower-case UUID,
* and `outcome` is one of {@link ORG_SEED_OUTCOMES}. Strict.
*/
const OrgSeedResultSchema = z.strictObject({
	orgId: z.string().regex(ORG_SEED_ORG_ID_PATTERN, "orgId must be a lower-case UUID"),
	outcome: z.enum(ORG_SEED_OUTCOMES)
});
/**
* The 200 body of `PUT /internal/v1/org-seeds`: `{ results }`, one {@link OrgSeedResult} per seed of
* the call that did not throw, 1 to {@link ORG_SEEDS_PER_CALL_MAX} rows, ascending by `orgId` in
* code unit order, each org id once. A seed that threw has no row (R-045 item 3).
*
* The schema checks the order and the count. It cannot see the request, so the operator program
* checks with {@link orgSeedsIngestResultWithin} that each row names an org id it sent (R-046
* item 1). {@link orgSeedsIngestResultCovers} tells whether the rows name exactly the org ids it
* sent. Strict.
*/
const OrgSeedsIngestResultSchema = z.strictObject({ results: z.array(OrgSeedResultSchema).min(1).max(32).refine((rows) => rows.every((row, index) => index === 0 || rows[index - 1].orgId < row.orgId), { message: "results ascend by orgId and each orgId appears once" }) });
/**
* Split an envelope list of seeds into the bodies of the seed door.
*
* Caller: the operator program (WS-10). Input: a list that parses with `orgSeedsSchema`, 0 to 1024
* seeds. Output: the list in its own order, cut into consecutive parts of
* {@link ORG_SEEDS_PER_CALL_MAX} seeds, the last part shorter when the count is not a multiple of 32.
* Each part is one `{ orgSeeds }` body that parses with {@link OrgSeedsIngestRequestSchema}. An
* empty list gives no body. The input is not changed. It never throws.
*/
function orgSeedsIngestRequests(seeds) {
	const requests = [];
	for (let start = 0; start < seeds.length; start += 32) requests.push({ orgSeeds: seeds.slice(start, start + 32) });
	return requests;
}
/**
* Tell whether an answer names exactly the seeds of its call.
*
* Callers: the contract suite of core-api's seed door (`org-seed-ingest.contract.ts`), to tell a
* full answer from a partial one. No production code calls it since R-046 item 1: the operator
* program calls {@link orgSeedsIngestResultWithin}, which accepts a partial answer.
*
* Input: a door body and a parsed answer. Output: `true` when the set of `orgId` values of the
* answer equals the set of `orgId` values of the body; `false` when the answer misses a seed or
* names an org that the body did not carry. It never throws.
*/
function orgSeedsIngestResultCovers(request, result) {
	const sent = request.orgSeeds.map((seed) => seed.orgId).sort();
	const answered = result.results.map((row) => row.orgId);
	return sent.length === answered.length && sent.every((orgId, index) => orgId === answered[index]);
}
/**
* Tell whether every row of an answer names a seed of its call (R-045 item 3, R-046 item 1).
*
* Caller: the operator program, after {@link OrgSeedsIngestResultSchema} parsed the answer. Input:
* the body it sent and the parsed answer. Output: `true` when each `orgId` of the answer is the
* `orgId` of a seed in the body, also when the answer leaves seeds out; `false` when a row names an
* org that the body did not carry. The operator program applies each row of a `true` answer and
* keeps each seed that the answer leaves out owed under its retry hold. A `false` answer is a
* failed call. It never throws.
*/
function orgSeedsIngestResultWithin(request, result) {
	const sent = new Set(request.orgSeeds.map((seed) => seed.orgId));
	return result.results.every((row) => sent.has(row.orgId));
}
//#endregion
export { ORG_SEEDS_INGEST_BODY_LIMIT_BYTES, ORG_SEEDS_INGEST_ROUTE, ORG_SEEDS_PER_CALL_MAX, ORG_SEED_FINAL_OUTCOMES, ORG_SEED_OUTCOMES, OrgSeedResultSchema, OrgSeedsIngestRequestSchema, OrgSeedsIngestResultSchema, isFinalOrgSeedOutcome, orgSeedsIngestRequests, orgSeedsIngestResultCovers, orgSeedsIngestResultWithin };

//# sourceMappingURL=org-seed-door.js.map